import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigurationClient } from '@azure/app-configuration';
import { BehaviorSubject, from, map, Observable, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import { BaseComponent } from '../support/base.component';
import { Configuration, FeatureFlags } from './configuration.service.model';

@Injectable()
//   {
//   providedIn: 'root'  // not using automatic injection into root module since we are creating it manually in main.ts bootstrapping
// }
export class ConfigurationService extends BaseComponent {
  private initialized: boolean = false;
  private configuration: Configuration | undefined;
  private _featureFlags: BehaviorSubject<FeatureFlags | null> =
    new BehaviorSubject<FeatureFlags | null>(null);
  public featureFlags$: Observable<FeatureFlags | null> = this._featureFlags.asObservable();

  private azAppConfigurationClient?: AppConfigurationClient;

  constructor(private httpClient: HttpClient) {
    super();
  }

  protected ngOnDestroyInternal(): void {
    // required by base component. clean up any component specific resources
  }

  public async initializeConfiguration(): Promise<void> {
    if (this.initialized) {
      console.debug('ConfigurationService already initialized. Skipping another fetch');
      return;
    }

    const configData = await this.httpClient
      .get<Configuration>('assets/configuration.json')
      .toPromise();

    if (!configData) {
      return;
    }

    this.configuration = {
      apiServiceEndpoint: configData.apiServiceEndpoint,
      apiScope: configData.apiScope,
      appInsightsKey: configData.appInsightsKey,
      msalConfig: {
        clientId: configData.msalConfig.clientId,
        authority: configData.msalConfig.authority,
        redirectUri: configData.msalConfig.redirectUri,
      },
      noAccessContactMessage: configData.noAccessContactMessage,
      instructionVideoUrl: configData.instructionVideoUrl,
      environmentName: configData.environmentName,
      labpartnerAppconfigCnn: configData.labpartnerAppconfigCnn,
      featureFlags: configData.featureFlags,
      icFusionLink: configData.icFusionLink,
      lutFileFormURl: configData.lutFileFormURl,
    };

    try {
      if (environment.testMode) {
        this._featureFlags.next(configData.featureFlags as FeatureFlags);
      } else {
        this.azAppConfigurationClient = new AppConfigurationClient(
          configData.labpartnerAppconfigCnn,
        );

        this.subscription.add(
          this.getAllFeatureFlags().subscribe(
            (featureFlags: any) => {
              console.log('Feature flags from App Configuration:', featureFlags);
              this._featureFlags.next(featureFlags);
            },
            error => {
              console.log('Error getting feature flags', error);
            },
          ),
        );
      }
    } catch (error) {
      console.error(
        'Failed to connect to labpartner-{environment}-appconfig azure resource, please double check connection string ',
        error,
      );
    }

    console.log('configuration initialized');

    this.initialized = true;
  }

  public getConfig(): Configuration {
    if (!this.initialized) {
      throw new Error('ConfigurationService must be initialized first.');
    }

    return this.configuration!;
  }

  public getFeatureFlags(): FeatureFlags | undefined {
    if (!this.initialized || !this.configuration) {
      throw new Error('ConfigurationService must be initialized first.');
    }

    return this.configuration.featureFlags ? this.configuration.featureFlags : ({} as FeatureFlags);
  }

  public getAllFeatureFlags(): Observable<{ [key: string]: any }> {
    if (!this.azAppConfigurationClient) {
      throw new Error('AppConfigurationClient is not initialized.');
    }

    return from(this.azAppConfigurationClient.listConfigurationSettings().byPage().next()).pipe(
      map(response => {
        const flagsList = response.value.items || [];
        const featureFlags: { [key: string]: any } = {};
        // Process the flagsList to extract feature flags
        flagsList.forEach((flag: any) => {
          if (flag.value && flag.key) {
            if (flag.key.includes('.appconfig.featureflag')) {
              // If the key contains '.appconfig.featureflag', it's a feature flag
              const key = flag.key.split('/').pop() || flag.key;
              const value = JSON.parse(flag.value).enabled;
              featureFlags[key] = value;
            } else {
              // If not a feature flag, just store the value as is
              try {
                featureFlags[flag.key] = JSON.parse(flag.value);
              } catch (e) {
                console.warn(`Failed to parse value for key: ${flag.key}`, e);
              }
            }
          }
        });

        return featureFlags; // Return the processed feature flags
      }),
      // Use shareReplay to cache the result and avoid multiple calls
      shareReplay(1),
    );
  }

  geticFusionLink(experimentId: number): string {
    if (this.configuration && this.configuration.icFusionLink) {
      return this.configuration.icFusionLink.replace('{{experimentId}}', experimentId.toString());
    } else {
      return '';
    }
  }
}
